import { ref } from 'vue'
import api from '@/services/Api'

interface PromotionCodeValidationResponse {
  valid: boolean
  message?: string
  promotion_code?: {
    id: string
    code: string
    active: boolean
    coupon: {
      id: string
      name: string
      percent_off: number | null
      amount_off: number | null
      currency: string | null
      duration: string
      duration_in_months: number | null
      valid: boolean
    }
  }
}

export function usePromotionCode() {
  const isValidatingCode = ref(false)
  const codeValidation = ref<{
    valid: boolean
    message?: string
    discount?: {
      percent_off: number | null
      amount_off: number | null
      currency: string | null
    }
  } | null>(null)

  const validatePromotionCode = async (code: string) => {
    if (!code) {
      codeValidation.value = null
      return
    }

    isValidatingCode.value = true
    codeValidation.value = null

    try {
      const apiResponse = await api().Post('/v3/payment/validate-promotion-code', {
        code: code,
      })
      const response = apiResponse.data as PromotionCodeValidationResponse

      if (response.valid && response.promotion_code) {
        codeValidation.value = {
          valid: true,
          discount: {
            percent_off: response.promotion_code.coupon.percent_off,
            amount_off: response.promotion_code.coupon.amount_off,
            currency: response.promotion_code.coupon.currency,
          },
        }
      } else {
        codeValidation.value = {
          valid: false,
          message: response.message || 'Invalid promotion code',
        }
      }
    } catch (error) {
      codeValidation.value = {
        valid: false,
        message: 'Failed to validate promotion code',
      }
    } finally {
      isValidatingCode.value = false
    }
  }

  const clearValidation = () => {
    codeValidation.value = null
  }

  const setValidation = (discount: { percent_off: number | null; amount_off: number | null; currency: string | null } | null) => {
    if (discount) {
      codeValidation.value = {
        valid: true,
        discount: discount
      }
    } else {
      codeValidation.value = null
    }
  }

  const formattedPrice = (price: number) => {
    return (price / 100).toLocaleString('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
    })
  }

  const formatDiscount = () => {
    const discount = codeValidation.value?.discount
    if (!discount) return ''

    if (discount.percent_off) {
      return `-${discount.percent_off}%`
    }
    if (discount.amount_off && discount.currency) {
      return `-${formattedPrice(discount.amount_off)}`
    }
    return ''
  }

  const calculateFinalPrice = (originalPrice: number) => {
    const discount = codeValidation.value?.discount

    if (!discount || !codeValidation.value?.valid) {
      return formattedPrice(originalPrice)
    }

    let finalPrice = originalPrice

    if (discount.percent_off) {
      finalPrice = originalPrice * (1 - discount.percent_off / 100)
    } else if (discount.amount_off) {
      finalPrice = Math.max(0, originalPrice - discount.amount_off)
    }

    return formattedPrice(finalPrice)
  }

  return {
    isValidatingCode,
    codeValidation,
    validatePromotionCode,
    clearValidation,
    setValidation,
    formatDiscount,
    formattedPrice,
    calculateFinalPrice,
  }
}
